import { axios } from '@Api/axiosClient';
import _ from 'lodash';
import type { FlowStep } from '@Shared';
import type { RequestDetails } from '@Types/RequestDetails.interface';
import type { AuthFlowState } from '@Types/AuthFlowState.interface';
import type { VpnStepUpDisplayReportResponse } from '@Types/VpnStepUpDisplayReportResponse.interface';
import { SessionExpiredError, isSessionGoneResponse } from '@Utils/sessionExpired';


export const getAuthFlowApi = async (authFlowName: string):Promise<FlowStep []> => {
    const response = await axios.get<FlowStep []>(
        `/api/AuthFlow/GetAuthFlow`,
        {
            params: { flowType: authFlowName }
        }
    );
    return response.data;
};

// A 404 means the session is gone and throws SessionExpiredError, because retrying
// cannot help. Any other failure is logged and resolves undefined, as before.
export const getNextAuthFlowApi = async ():Promise<FlowStep [] | undefined> => {
    try
    {
        const response = await axios.get<FlowStep []>(
            `/api/AuthFlow/GetNextAuthFlow`
        );
        return response.data;
    }
    catch(error)
    {
        if (isSessionGoneResponse(error)) {
            throw new SessionExpiredError('GetNextAuthFlow');
        }
        console.log(error);
        return undefined;
    }
};


export const getRequestDetails = async() : Promise<RequestDetails> => {
    const response = await axios.get(
        '/api/AuthFlow/GetRequestContext'
    );

    return response.data;
};

// Throws SessionExpiredError on a 404 (session gone); other failures throw as before.
export const storeMfeRoute = async(redirectUrl: string) => {
    try {
        await axios.post(
            '/api/AuthFlow/StoreCompletionRedirectUrl',
            redirectUrl
        );
    } catch (error) {
        if (isSessionGoneResponse(error)) {
            throw new SessionExpiredError('StoreCompletionRedirectUrl');
        }
        throw error;
    }
};

// Throws SessionExpiredError on a 404 (session gone); other failures throw as before.
export const getStoredMfeRoute = async() : Promise<string> => {
    try {
        const response = await axios.get(
            '/api/AuthFlow/GetCompletionRedirectUrl',
        );

        return response.data;
    } catch (error) {
        if (isSessionGoneResponse(error)) {
            throw new SessionExpiredError('GetCompletionRedirectUrl');
        }
        throw error;
    }
};
// Report that the DisableVpn step-up view was displayed.
// The displayId is a fresh per-display UUID; all session context is resolved
// server-side. A 200 carries the attempt ordinal for this display (used to
// pick the copy variant); failure bodies are empty, so any error — network
// or non-200 — is logged here and surfaces as undefined, and the caller
// keeps its current ordinal.
export const reportVpnStepUpDisplayApi = async (displayId: string): Promise<VpnStepUpDisplayReportResponse | undefined> => {
    try {
        const response = await axios.post<VpnStepUpDisplayReportResponse>(
            '/api/AuthFlow/ReportVpnStepUpDisplay',
            { displayId }
        );
        return response.data;
    } catch (error) {
        console.error('[Auth MFE] ReportVpnStepUpDisplay failed:', error);
        return undefined;
    }
};

// A 404 means the session is gone and throws SessionExpiredError, because retrying
// cannot help. Any other failure is logged and resolves undefined, as before.
export const getAuthFlowStatusApi = async (): Promise<AuthFlowState | undefined> => {
    try {
        const response = await axios.get<AuthFlowState>('/api/AuthFlow/GetAuthFlowStatus');
        return response.data;
    } catch (error) {
        if (isSessionGoneResponse(error)) {
            throw new SessionExpiredError('GetAuthFlowStatus');
        }
        console.log(error);
        return undefined;
    }
};

